<script setup lang="ts">
import type { FormkitCommonProps } from "@/components/ui/input/formkit/formkit.types";
import type { TMissionDto } from "@/types/backoffice/dto/mission/mission-dto";
import type { FormKitFrameworkContext } from "@formkit/core";
import type { PropType } from "vue";

import { useQuery } from "@tanstack/vue-query";

import BackofficeApi from "@/api/backoffice/backoffice-api.ts";
import { OperationsQueryKeys } from "@/api/backoffice/operations/operations-query-keys.ts";
import { DATA_TEST_IDS } from "@/constants/data-test-ids";
import { useFormkitComputedValue } from "@/hooks/common/use-formkit";

/**
 * @deprecated Use non-formkit-specific MissionSelect instead or its wrapped version MissionDropdownInput
 */
import Dropdown from "primevue/dropdown";
import Skeleton from "primevue/skeleton";

export type TMissionDropdownProps = FormkitCommonProps;

const props = defineProps({
  context: {
    type: Object as PropType<FormKitFrameworkContext & TMissionDropdownProps>,
  },
});

const model = useFormkitComputedValue<TMissionDto>(props.context);

const { data: missions, isLoading: isMissionLoading } = useQuery({
  queryKey: OperationsQueryKeys.findAllMissions(),
  queryFn: () => BackofficeApi.findMissions(),
});
</script>

<template>
  <Skeleton
    v-if="isMissionLoading"
    class="w-full h-full"
    style="height: 3rem"
    :data-test-id="DATA_TEST_IDS.components.missionDropdown.skeleton()"
  />
  <Dropdown
    v-else
    v-model="model"
    :loading="isMissionLoading"
    :options="missions"
    :disabled="props.context?.disabled"
    :name="props.context?.name"
    data-key="id"
    :option-label="(mission: TMissionDto) => mission.note"
    :placeholder="props.context?.placeholder"
    class="w-full"
    v-bind="$attrs"
    :data-test-id="DATA_TEST_IDS.components.missionDropdown.input()"
  />
</template>

<style scoped></style>
